import React from "react";
import Noise from "../../../components/animations/Noise.jsx";
import WavyText from "../../../components/animations/WavyText.jsx";

import { FaReact } from "react-icons/fa";
import { RiNextjsLine } from "react-icons/ri";
import { IoLogoNodejs } from "react-icons/io";

import {
  SiExpress,
  SiMongodb,
  SiPostgresql,
  SiFirebase,
  SiTailwindcss,
  SiTypescript,
  SiJavascript,
  SiFigma,
  SiRedux,
  SiGit,
  SiPython,
  SiDjango,
  SiDocker,
  SiKubernetes,
} from "react-icons/si";

/* -------------------------------------------------------------------------- */
/* TECHNOLOGIES                                                               */
/* -------------------------------------------------------------------------- */

const technologies = [
  {
    name: "React.js",
    desc: "Interactive frontend library for modern web applications.",
    icon: FaReact,
  },
  {
    name: "Next.js",
    desc: "Full-stack React framework with SSR and optimized performance.",
    icon: RiNextjsLine,
  },
  {
    name: "Node.js",
    desc: "Backend runtime environment for scalable server-side applications.",
    icon: IoLogoNodejs,
  },
  {
    name: "Express.js",
    desc: "Minimal and flexible Node.js framework for APIs and backend services.",
    icon: SiExpress,
  },
  {
    name: "MongoDB",
    desc: "NoSQL database designed for flexibility and scalability.",
    icon: SiMongodb,
  },
  {
    name: "PostgreSQL",
    desc: "Advanced relational database for secure enterprise applications.",
    icon: SiPostgresql,
  },
  {
    name: "Firebase",
    desc: "Backend services for authentication, hosting, and realtime databases.",
    icon: SiFirebase,
  },
  {
    name: "Tailwind CSS",
    desc: "Utility-first CSS framework for rapid UI development.",
    icon: SiTailwindcss,
  },
  {
    name: "TypeScript",
    desc: "Strongly typed JavaScript for scalable application development.",
    icon: SiTypescript,
  },
  {
    name: "JavaScript",
    desc: "Core programming language powering modern web experiences.",
    icon: SiJavascript,
  },
  {
    name: "Docker",
    desc: "Containerization platform for consistent deployments.",
    icon: SiDocker,
  },
  {
    name: "Kubernetes",
    desc: "Container orchestration system for scalable infrastructure.",
    icon: SiKubernetes,
  },
  {
    name: "Python",
    desc: "Modern programming language for powerful application development.",
    icon: SiPython,
  },
  {
    name: "Django",
    desc: "Modern Python framework for secure and scalable web applications.",
    icon: SiDjango,
  },
  {
    name: "Figma",
    desc: "Collaborative UI/UX design and prototyping platform.",
    icon: SiFigma,
  },
  {
    name: "Redux",
    desc: "State management library for predictable application state.",
    icon: SiRedux,
  },
  {
    name: "Git",
    desc: "Version control system for collaborative development workflows.",
    icon: SiGit,
  },
];

/* -------------------------------------------------------------------------- */
/* SPLIT TECHNOLOGIES INTO TWO ROWS                                           */
/* -------------------------------------------------------------------------- */

const midpoint = Math.ceil(technologies.length / 2);

const firstRow = technologies.slice(0, midpoint);
const secondRow = technologies.slice(midpoint);

/* -------------------------------------------------------------------------- */
/* TECHNOLOGY CARD                                                            */
/* -------------------------------------------------------------------------- */

const TechCard = ({ name, desc, icon: Icon }) => {
  return (
    <article
      className="
        group
        flex
        w-[220px]
        shrink-0
        flex-col

        rounded-xl
        border
        theme-border
        theme-card

        px-4
        py-4
        mx-1.5

        transition-all
        duration-300

        hover:-translate-y-0.5

        sm:w-[245px]
        sm:px-4.5
        sm:py-4
        sm:mx-2
        sm:rounded-2xl

        md:w-[270px]
        md:px-5
        md:py-5

        lg:w-[285px]
        lg:px-5
        lg:py-5
      "
    >
      {/* ------------------------------------------------------------------ */}
      {/* CARD HEADER                                                        */}
      {/* ------------------------------------------------------------------ */}

      <div
        className="
          flex
          min-w-0
          items-center
          gap-2.5

          sm:gap-3
        "
      >
        {/* ICON */}

        <div
          className="
            flex
            h-9
            w-9
            shrink-0
            items-center
            justify-center

            rounded-lg

            border
            brand-border

            bg-[rgba(249,115,22,0.10)]
            brand-color

            transition-all
            duration-300

            group-hover:bg-[rgba(249,115,22,0.15)]

            sm:h-10
            sm:w-10
            sm:rounded-xl

            md:h-11
            md:w-11
          "
        >
          <Icon
            className="
              h-[19px]
              w-[19px]

              sm:h-5
              sm:w-5

              md:h-[22px]
              md:w-[22px]
            "
          />
        </div>

        {/* NAME */}

        <h3
          className="
            min-w-0
            truncate

            text-sm
            font-bold
            tracking-wide

            theme-text-primary

            transition-colors
            duration-300

            group-hover:brand-color

            sm:text-[15px]

            md:text-base
          "
        >
          {name}
        </h3>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* DESCRIPTION                                                        */}
      {/* ------------------------------------------------------------------ */}

      <p
        className="
          mt-2.5

          line-clamp-2

          text-[10px]
          leading-[1.5]

          theme-text-muted

          transition-colors
          duration-300

          sm:mt-3
          sm:text-[11px]
          sm:leading-[1.55]

          md:text-xs
          md:leading-relaxed
        "
      >
        {desc}
      </p>
    </article>
  );
};

/* -------------------------------------------------------------------------- */
/* MARQUEE ROW                                                                */
/* -------------------------------------------------------------------------- */

const MarqueeRow = ({ items, reverse = false }) => {
  /*
    Duplicate the row so the transform can continuously loop
    without a visible gap.
  */
  const duplicatedItems = [...items, ...items];

  return (
    <div
      className="
        group
        relative
        flex
        w-full
        overflow-hidden
      "
    >
      <div
        className={`
          flex
          w-max
          items-stretch

          ${
            reverse
              ? "animate-tech-marquee-reverse"
              : "animate-tech-marquee"
          }

          group-hover:[animation-play-state:paused]

          motion-reduce:animate-none
        `}
      >
        {duplicatedItems.map((tech, index) => (
          <TechCard
            key={`${tech.name}-${index}`}
            {...tech}
          />
        ))}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                             */
/* -------------------------------------------------------------------------- */

const TechMarquee = () => {
  return (
    <section
      className="
        relative
        w-full
        overflow-hidden

        theme-bg-primary
        theme-text-primary

        font-saira

        py-7

        sm:py-8

        md:py-9

        lg:py-10

        xl:py-11
      "
    >
      {/* ================================================================== */}
      {/* NOISE BACKGROUND                                                    */}
      {/* ================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          z-[1]
          overflow-hidden
          opacity-70
        "
      >
        <Noise
          patternSize={250}
          patternScaleX={2}
          patternScaleY={2}
          patternRefreshInterval={2}
          patternAlpha={15}
        />
      </div>

      {/* ================================================================== */}
      {/* SUBTLE TOP GLOW                                                     */}
      {/* ================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          z-[1]

          h-[160px]
          w-[260px]

          -translate-x-1/2

          rounded-full

          blur-[100px]

          sm:h-[190px]
          sm:w-[340px]

          md:h-[220px]
          md:w-[460px]

          lg:h-[260px]
          lg:w-[600px]
        "
        style={{
          backgroundColor: "color-mix(in srgb, var(--brand-primary) 4%, transparent)",
        }}
      />

      {/* ================================================================== */}
      {/* CONTENT LAYER                                                       */}
      {/* ================================================================== */}

      <div
        className="
          relative
          z-10
          w-full
        "
      >
        {/* ================================================================= */}
        {/* HEADER                                                             */}
        {/* ================================================================= */}

        <header
          className="
            mx-auto
            mb-7
            w-full

            px-4
            text-center

            sm:mb-8
            sm:px-6

            md:mb-9
            md:px-8

            lg:mb-10
            lg:px-10

            xl:mb-11
          "
        >
          {/* EYEBROW */}

          <span
            className="
              inline-flex
              max-w-full
              items-center
              justify-center

              text-[9px]
              font-bold
              uppercase
              tracking-[0.18em]

              brand-color

              sm:text-[10px]
              sm:tracking-[0.2em]

              md:text-xs
              md:tracking-[0.22em]
            "
          >
            [ Our Technologies ]
          </span>

          {/* HEADING */}

          <h2
            className="
              mx-auto
              mt-2.5
              w-full
              max-w-[95vw]

              text-2xl
              font-semibold
              leading-tight
              tracking-tight

              theme-text-primary

              sm:mt-3
              sm:max-w-2xl
              sm:text-3xl

              md:text-4xl

              lg:max-w-4xl
              lg:text-5xl

              xl:text-[52px]
            "
          >
            <WavyText text="Technologies Used By Us" />
          </h2>
        </header>

        {/* ================================================================= */}
        {/* MARQUEE AREA                                                       */}
        {/* ================================================================= */}

        <div
          className="
            relative
            w-full

            space-y-3

            sm:space-y-4

            md:space-y-5

            lg:space-y-6
          "
        >
          {/* =============================================================== */}
          {/* ROW 1                                                            */}
          {/* =============================================================== */}

          <MarqueeRow items={firstRow} />

          {/* =============================================================== */}
          {/* ROW 2                                                            */}
          {/* =============================================================== */}

          <MarqueeRow
            items={secondRow}
            reverse
          />

          {/* =============================================================== */}
          {/* LEFT FADE                                                        */}
          {/* =============================================================== */}

          <div
            className="
              pointer-events-none
              absolute
              inset-y-0
              left-0
              z-20

              w-16

              bg-gradient-to-r

              from-[var(--bg-primary)]
              via-[var(--bg-primary)]/80
              to-transparent

              sm:w-20

              md:w-28

              lg:w-40

              xl:w-52
            "
          />

          {/* =============================================================== */}
          {/* RIGHT FADE                                                       */}
          {/* =============================================================== */}

          <div
            className="
              pointer-events-none
              absolute
              inset-y-0
              right-0
              z-20

              w-16

              bg-gradient-to-l

              from-[var(--bg-primary)]
              via-[var(--bg-primary)]/80
              to-transparent

              sm:w-20

              md:w-28

              lg:w-40

              xl:w-52
            "
          />
        </div>

        {/* ================================================================= */}
        {/* RESPONSIVE MARQUEE CSS                                             */}
        {/* ================================================================= */}

        <style>{`
          @keyframes tech-marquee {
            0% {
              transform: translate3d(0, 0, 0);
            }

            100% {
              transform: translate3d(-50%, 0, 0);
            }
          }

          @keyframes tech-marquee-reverse {
            0% {
              transform: translate3d(-50%, 0, 0);
            }

            100% {
              transform: translate3d(0, 0, 0);
            }
          }

          .animate-tech-marquee {
            animation: tech-marquee 28s linear infinite;
            will-change: transform;
          }

          .animate-tech-marquee-reverse {
            animation: tech-marquee-reverse 28s linear infinite;
            will-change: transform;
          }

          /* --------------------------------------------------------------- */
          /* SMALL PHONES                                                    */
          /* --------------------------------------------------------------- */

          @media (max-width: 480px) {
            .animate-tech-marquee {
              animation-duration: 24s;
            }

            .animate-tech-marquee-reverse {
              animation-duration: 24s;
            }
          }

          /* --------------------------------------------------------------- */
          /* TABLETS                                                         */
          /* --------------------------------------------------------------- */

          @media (min-width: 640px) {
            .animate-tech-marquee {
              animation-duration: 30s;
            }

            .animate-tech-marquee-reverse {
              animation-duration: 30s;
            }
          }

          /* --------------------------------------------------------------- */
          /* LARGE DESKTOP                                                   */
          /* --------------------------------------------------------------- */

          @media (min-width: 1280px) {
            .animate-tech-marquee {
              animation-duration: 34s;
            }

            .animate-tech-marquee-reverse {
              animation-duration: 34s;
            }
          }

          /* --------------------------------------------------------------- */
          /* REDUCED MOTION                                                   */
          /* --------------------------------------------------------------- */

          @media (prefers-reduced-motion: reduce) {
            .animate-tech-marquee,
            .animate-tech-marquee-reverse {
              animation: none !important;
              transform: translate3d(0, 0, 0) !important;
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default TechMarquee;